import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery } from "@apollo/client";
import { Alert, Button, ListGroup, ListGroupItem } from "reactstrap";
import * as Notify from "@repoflow/form-schema/notify";
import * as Loading from "@repoflow/components/loading";
import * as CupboardQL from "queries/cupboard";
import "components/cupboard/cupboard.css";

// The cupboard as a list a person corrects: each item renamed in place or
// taken out. Things mostly go IN through the chat ("I have eggs and rice"),
// which Claude reads into items. By hand it is ONE item at a time (`AddOne`),
// and not saved as typed: "I have eggs" once became an item called "I have
// eggs". The server checks it is food and tidies it — spelling, an amount
// split off, merged into the same food already listed — and a list is turned
// back to the chat. The same list is the cupboard screen and the
// chat's Cupboard tab (`compact`), so a change in one is in the other: every
// mutation answers with the whole cupboard under its cached id, and so does a
// chat reply that changed it.
//
// What is in matters, not how much: an amount is an optional note on an item,
// set from its edit line.
//
// No confirm on remove. An item is one line to type back, and a browser
// dialog would be the heaviest thing on the page.
const NAME_MAX = 100;
const QUANTITY_MAX = 60;

const onError = (error) => Notify.error(String(error));

export const useCupboard = () => {
  const { data, loading, error } = useQuery(CupboardQL.get, { fetchPolicy: "cache-and-network" });
  return { cupboard: data?.viewer?.account?.cupboard?.get, loading, error };
};

// One item, as the add line takes it, or why it will not: a list ("eggs,
// rice") is several, and several at once is the chat's to read. Anything else
// — "brocoli", "6 eggs", even "I have eggs" — goes to the server, where Jev
// says whether it is food and Claude tidies it (model/cupboard.addChecked).
const LIST = /[,;\n]/;

export const oneItem = (text) => {
  const name = String(text || "").trim().replace(/\s+/g, " ");
  if (!name) return { name: "", problem: null };
  if (LIST.test(name)) return { name, problem: "One item at a time. For several, tell the chat." };
  return { name: name.slice(0, NAME_MAX), problem: null };
};

// What the add line says once the server has answered.
export const resultOf = ({ outcome, typed, name, quantity }) => {
  if (outcome === "not-food") return { ok: false, text: `"${typed}" doesn't look like food, so it wasn't added.` };
  const what = `${name}${quantity ? ` (${quantity})` : ""}`;
  if (outcome === "merged") return { ok: true, text: quantity ? `You already had ${name}: now ${quantity}.` : `You already had ${name}.` };
  return { ok: true, text: `Added ${what}.` };
};

const AddOne = () => {
  const [text, setText] = useState("");
  const [said, setSaid] = useState(null);
  const [add, { loading }] = useMutation(CupboardQL.addChecked, { onError });
  const { name, problem } = oneItem(text);

  const submit = async (event) => {
    event.preventDefault();
    if (!name || problem || loading) return;
    setSaid(null);
    const { data, errors } = await add({ variables: { input: { text: name } } });
    const answer = data?.viewer?.account?.cupboard?.addChecked;
    if (errors || !answer) return;
    setSaid(resultOf(answer));
    if (answer.outcome !== "not-food") setText("");
  };

  return (
    <form onSubmit={submit} className="mb-2" data-testid={"cupboard-add-form"}>
      <div className="d-flex align-items-center noshchat-cupboard__line">
        <div className="noshchat-cupboard__name">
          <input
            className={`form-control form-control-sm${problem ? " is-invalid" : ""}`}
            aria-label={"Add one item"}
            placeholder={"Add one item, e.g. 6 eggs"}
            maxLength={NAME_MAX}
            value={text}
            onChange={(e) => {
              setText(e.target.value);
              setSaid(null);
            }}
            disabled={loading}
            data-testid={"cupboard-name-input"}
          />
        </div>
        <Button type={"submit"} color={"primary"} size={"sm"} className="text-nowrap" disabled={!name || Boolean(problem) || loading} data-testid={"cupboard-add"}>
          {loading ? <><i className="fa fa-spinner fa-spin" /> Checking…</> : <><i className="fa fa-plus" /> Add item</>}
        </Button>
      </div>
      {problem ? (
        <div className="small text-danger mt-1" data-testid={"cupboard-add-problem"}>
          {problem}
        </div>
      ) : said ? (
        <div className={`small mt-1 ${said.ok ? "text-success" : "text-danger"}`} data-testid={"cupboard-add-result"}>
          {said.text}
        </div>
      ) : null}
    </form>
  );
};

const Item = ({ item }) => {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(item.name);
  const [quantity, setQuantity] = useState(item.quantity);
  const [update, { loading: saving }] = useMutation(CupboardQL.update, { onError });
  const [remove, { loading: removing }] = useMutation(CupboardQL.remove, { onError });
  const busy = saving || removing;

  const edit = () => {
    setName(item.name);
    setQuantity(item.quantity);
    setEditing(true);
  };

  const save = async (event) => {
    event.preventDefault();
    if (!name.trim() || busy) return;
    const { errors } = await update({
      variables: { input: { itemId: item.itemId, name: name.trim(), quantity: quantity.trim() } },
    });
    if (!errors) setEditing(false);
  };

  if (editing) {
    return (
      <ListGroupItem data-testid={"cupboard-item"}>
        <form onSubmit={save}>
          <div className="d-flex flex-wrap align-items-center noshchat-cupboard__line">
            <div className="noshchat-cupboard__name">
              <input
                className="form-control form-control-sm"
                aria-label={"Food"}
                maxLength={NAME_MAX}
                value={name}
                onChange={(e) => setName(e.target.value)}
                data-testid={"cupboard-edit-name"}
                autoFocus
              />
            </div>
            <div className="noshchat-cupboard__quantity">
              <input
                className="form-control form-control-sm"
                aria-label={"How much (optional)"}
                placeholder={"How much (optional)"}
                maxLength={QUANTITY_MAX}
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                data-testid={"cupboard-edit-quantity"}
              />
            </div>
            <div className="text-nowrap">
              <Button type={"submit"} color={"primary"} size={"sm"} disabled={!name.trim() || busy} data-testid={"cupboard-save"}>
                Save
              </Button>{" "}
              <Button type={"button"} color={"link"} size={"sm"} onClick={() => setEditing(false)}>
                Cancel
              </Button>
            </div>
          </div>
        </form>
      </ListGroupItem>
    );
  }

  return (
    <ListGroupItem className="d-flex align-items-center" data-testid={"cupboard-item"}>
      <div className="flex-grow-1 text-truncate">
        <span data-testid={"cupboard-item-name"}>{item.name}</span>
        {item.quantity ? (
          <span className="text-muted small ml-2" data-testid={"cupboard-item-quantity"}>
            {item.quantity}
          </span>
        ) : null}
      </div>
      <Button color={"link"} size={"sm"} className="px-1" onClick={edit} disabled={busy} aria-label={`Change ${item.name}`} data-testid={"cupboard-edit"}>
        <i className="fa fa-pencil" />
      </Button>
      <Button
        color={"link"}
        size={"sm"}
        className="px-1 text-danger"
        onClick={() => remove({ variables: { input: { itemId: item.itemId } } })}
        disabled={busy}
        aria-label={`Remove ${item.name}`}
        data-testid={"cupboard-remove"}
      >
        <i className="fa fa-trash" />
      </Button>
    </ListGroupItem>
  );
};

// Where adding several at once happens. Beside the chat it says so; elsewhere
// it links there.
const ToChat = ({ compact }) => (
  <p className="text-muted small mb-2" data-testid={"cupboard-to-chat"}>
    {compact ? (
      <>To add several at once, tell the chat, e.g. "I have eggs and rice".</>
    ) : (
      <>
        To add several at once, <Link to={`/chat`}>tell the chat</Link>, e.g. "I have eggs and rice".
      </>
    )}
  </p>
);

export const CupboardList = ({ compact = false }) => {
  const { cupboard, loading, error } = useCupboard();
  const items = cupboard?.items || [];

  return (
    <div className="noshchat-cupboard" data-testid={"cupboard-list"}>
      <AddOne />
      <ToChat compact={compact} />

      {error ? (
        <Alert color={"danger"} className="mt-2">
          The cupboard could not be read. {error.message}
        </Alert>
      ) : !cupboard && loading ? (
        <div className="mt-3"><Loading.Icon /></div>
      ) : !items.length ? (
        <p className="text-muted small mt-2 mb-0" data-testid={"cupboard-empty"}>
          Nothing here yet.
        </p>
      ) : (
        <ListGroup flush={compact} className="noshchat-cupboard__items mt-1">
          {items.map((item) => (
            <Item key={`${item.itemId}-${item.updatedAt}`} item={item} />
          ))}
        </ListGroup>
      )}
    </div>
  );
};
